/* eslint-disable no-case-declarations */
import React, { useContext } from 'react';
import { EllipsisOutlined } from '@ant-design/icons';
import { Button, Dropdown, MenuProps } from 'antd';

import TaskContext from '@totem/components/ticketing/details/tasks/taskContext';
import TicketContext from '@totem/components/ticketing/details/ticketContext';
import { ticketPermissions } from '@totem/components/ticketing/permissions';
import {
  HierarchicalTask,
  TASK_TYPE_GROUP,
} from '@totem/components/ticketing/ticketing';
import { TaskCreateExtras } from '@totem/components/ticketing/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import { isNotNull } from '@totem/utilities/common';

type Props = {
  task: HierarchicalTask;
};

const TaskActionMenu = ({ task }: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const { ticket, onAction } = useContext(TicketContext);
  const { onTaskAction, getPreviousSibling, getNextSibling } =
    useContext(TaskContext);

  const previousSibling = getPreviousSibling(task);
  const nextSibling = getNextSibling(task);

  const canGoUp = isNotNull(previousSibling);
  const canGoDown = isNotNull(nextSibling);
  const canGoRight =
    isNotNull(previousSibling) && previousSibling.type === TASK_TYPE_GROUP;
  const canGoLeft = task.parent !== 0;

  const getActionMenuItems = (): MenuProps['items'] => {
    const menuItems: MenuProps['items'] = [];

    if (
      ticketPermissions.taskEditor.hasPermissions(userProfile, ticket, task)
    ) {
      if (canGoUp || canGoDown || canGoLeft || canGoRight) {
        menuItems.push({
          key: 'move',
          label: 'Move',
          title: 'Move',
          children: [
            canGoDown && {
              key: 'move_down',
              label: 'Down',
              title: 'Down',
            },
            canGoRight && {
              key: 'move_right',
              label: 'Right',
              title: 'Right',
            },
            canGoUp && {
              key: 'move_up',
              label: 'Up',
              title: 'UP',
            },
            canGoLeft && {
              key: 'move_left',
              label: 'Left',
              title: 'Left',
            },
          ],
        });
      }
    }

    if (
      ticketPermissions.taskEditor.hasPermissions(userProfile, ticket, task)
    ) {
      menuItems.push({
        key: 'edit',
        label: 'Edit',
        title: 'Edit',
        icon: <IBDIcon name={'Edit'} style={{ marginRight: '8px' }} />,
      });
    }

    if (
      ticketPermissions.taskEditor.hasPermissions(userProfile, ticket, task)
    ) {
      menuItems.push({
        key: 'remove',
        label: 'Remove',
        title: 'Remove',
        icon: <IBDIcon name={'Delete'} style={{ marginRight: '8px' }} />,
      });
    }

    return menuItems;
  };

  const onMenuClicked = ({ key }) => {
    // eslint-disable-next-line default-case
    switch (key) {
      case 'move_down':
        onTaskAction('move-task-down', JSON.stringify(task));
        break;
      case 'move_right':
        onTaskAction('move-task-right', JSON.stringify(task));
        break;
      case 'move_up':
        onTaskAction('move-task-up', JSON.stringify(task));
        break;
      case 'move_left':
        onTaskAction('move-task-left', JSON.stringify(task));
        break;
      case 'edit':
        const extras: TaskCreateExtras = {
          editTask: task,
        };
        onAction('show-task-create', JSON.stringify(extras));
        break;
      case 'remove':
        onTaskAction('do-task-delete', JSON.stringify(task));
        break;
    }
  };

  return (
    <Dropdown
      menu={{ items: getActionMenuItems(), onClick: onMenuClicked }}
      trigger={['click']}
    >
      <Button shape="circle" icon={<EllipsisOutlined />} />
    </Dropdown>
  );
};

export default TaskActionMenu;
